/**
 * Memory API Service (character-guided profiling — WU-W0).
 * The REST client for the remembrance backbone. On error, methods log and return
 * the null/empty failure signal (the revert/empty signal every consumer relies on).
 */

import { apiService } from './apiService';
import type {
  UserMemory,
  MemoryStatus,
  MemoryKind,
  CreateMemoryRequest,
  UserTraitProfile,
} from '../../types/memory';

/** GET /api/profile/memories — caller-scoped list (default = confirmed + proposed). */
async function getMemories(params?: {
  status?: MemoryStatus;
  npcId?: string;
  kind?: MemoryKind;
}): Promise<UserMemory[]> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.npcId) qs.set('npcId', params.npcId);
  if (params?.kind) qs.set('kind', params.kind);
  const query = qs.toString();
  const response = await apiService.get<{ memories: UserMemory[] }>(
    `/api/profile/memories${query ? `?${query}` : ''}`
  );
  if (response.error) {
    console.error('Failed to get memories:', response.error);
    return [];
  }
  return response.data?.memories ?? [];
}

/** POST /api/profile/memories — confirm/decline write; idempotent on clientRef. */
async function createMemory(body: CreateMemoryRequest): Promise<UserMemory | null> {
  const response = await apiService.post<{ memory: UserMemory }>('/api/profile/memories', body);
  if (response.error) {
    console.error('Failed to create memory:', response.error);
    return null;
  }
  return response.data?.memory ?? null;
}

/** PUT /api/profile/memories/{id} — transition a persisted row (confirm/reject/burn). */
async function updateMemoryStatus(
  id: string,
  status: MemoryStatus,
  suppressTopicFamily?: boolean
): Promise<UserMemory | null> {
  const response = await apiService.put<{ memory: UserMemory }>(
    `/api/profile/memories/${id}`,
    { status, suppressTopicFamily }
  );
  if (response.error) {
    console.error('Failed to update memory status:', response.error);
    return null;
  }
  return response.data?.memory ?? null;
}

/** GET /api/profile/traits — caller's trait profile (always 7 dims; never 404). */
async function getTraits(): Promise<UserTraitProfile | null> {
  const response = await apiService.get<UserTraitProfile>('/api/profile/traits');
  if (response.error) {
    console.error('Failed to get traits:', response.error);
    return null;
  }
  return response.data ?? null;
}

export const memoryService = {
  getMemories,
  createMemory,
  updateMemoryStatus,
  getTraits,
};
